'use client'

import { Component, type ReactNode } from 'react'

import { sentryCall } from '@/lib/log/sentry-client'

// Stamped on a caught error so the route-level error.tsx doesn't ALSO report it
// (this boundary already did, with the React componentStack attached). Read in
// (story-pages)/error.tsx.
export interface ComponentStackReportedError extends Error {
  __componentStackReported?: boolean
}

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

// A pass-through error boundary whose ONLY job is observability: when a client
// component in its subtree throws during render/hydration, report it to Sentry
// WITH React's componentStack — the repeating component names that pinpoint a
// recursive-render "Maximum call stack" (HT-APP-6K and its higher-volume
// sibling), which Next's error.tsx can't surface because it only receives
// `{ error, reset }`. Then re-throw so the nearest route error.tsx still owns the
// fallback UI. Renders nothing of its own.
//
// Only catches render/hydration-phase errors — event-handler/async overflows are
// invisible to any React boundary and fall through to the browser's global
// handler instead (helped by the raised Error.stackTraceLimit in
// src/instrumentation-client.ts).
export class ComponentStackBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: ComponentStackReportedError, info: { componentStack?: string }): void {
    error.__componentStackReported = true
    sentryCall((Sentry) =>
      Sentry.captureException(error, { contexts: { react: { componentStack: info.componentStack } } })
    )
  }

  render(): ReactNode {
    if (this.state.error) {
      // Re-throw so the route-level error.tsx renders the (shared, i18n'd)
      // fallback; we only wanted to enrich the report with the componentStack.
      throw this.state.error
    }
    return this.props.children
  }
}
